import { learnerReturnPath } from 'modules/auth/return-path';
import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { TFunction } from 'i18next';
import { UserCheck, ArrowRight, ArrowLeft, Phone } from 'lucide-react';
import { Button } from 'components/ui/button';
import {
  AccountTypePicker,
  AuthSplitPanel,
  AuthField,
  AuthPhoneField,
  AuthPasswordField,
  AuthStepper,
  GoogleSignInButton,
  TelegramBotLogin,
  TelegramMiniAppButton,
  OtpVerify,
} from 'components/auth';
import { isTelegram } from 'lib/telegram';
import { IS_IO } from 'lib/site';
import {
  useStartPhoneRegister,
  useResendPhoneRegister,
  useVerifyPhoneRegister,
  useCompletePhoneRegister,
  type TelegramBotLoginPhase,
} from 'modules/auth';
import { useToast } from 'components/ui/use-toast';
import { useTranslation } from 'react-i18next';
import { UZ_E164_PATTERN } from 'lib/phone';
import { acquisitionPayload } from 'lib/acquisition';

type WizardStep = 'phone' | 'verify' | 'password';

const makePhoneSchema = (t: TFunction) =>
  z.object({
    phone: z.string().regex(UZ_E164_PATTERN, t('auth.invalid_phone')),
  });
type PhoneFormData = z.infer<ReturnType<typeof makePhoneSchema>>;

const makePasswordSchema = (t: TFunction) =>
  z
    .object({
      name: z.string().optional(),
      password: z.string().min(8, t('auth.password_min')),
      confirmPassword: z.string().min(8, t('auth.password_min')),
    })
    .refine((d) => d.password === d.confirmPassword, {
      path: ['confirmPassword'],
      message: t('auth.passwords_no_match'),
    });
type PasswordFormData = z.infer<ReturnType<typeof makePasswordSchema>>;

// The wizard is stashed in sessionStorage so a refresh on the verify/password
// step resumes there instead of dropping back to phone (mirrors the recovery
// flow in ForgotPassword). Tab-scoped and short-lived, which suits a signup
// challenge. Only the resumable fields — never the password or the raw code.
const SIGNUP_STORAGE_KEY = 'examing.signup';

interface SignupSession {
  step: 'verify' | 'password';
  phone: string;
  phoneHint: string;
  challengeRef: string;
  accountType: 'STAFF' | 'CANDIDATE';
  /** Preserve an invite token across a refresh (team-join has its own fallback). */
  inviteToken?: string;
  orgInviteCode?: string;
}

const readSignupSession = (): SignupSession | null => {
  try {
    const raw = sessionStorage.getItem(SIGNUP_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SignupSession>;
    if (!parsed.challengeRef || (parsed.step !== 'verify' && parsed.step !== 'password')) return null;
    return parsed as SignupSession;
  } catch {
    return null;
  }
};

const writeSignupSession = (session: SignupSession) => {
  try {
    sessionStorage.setItem(SIGNUP_STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Storage unavailable (private mode / quota) — the flow still works in
    // memory for the current page, it just won't survive a refresh.
  }
};

const clearSignupSession = () => {
  try {
    sessionStorage.removeItem(SIGNUP_STORAGE_KEY);
  } catch {
    // ignore
  }
};

const Signup: React.FC = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const navState =
    (location.state as { inviteToken?: string; teamJoinToken?: string; orgInviteCode?: string; referralCode?: string }) || {};
  // Restore an in-flight signup once, on mount, so a refresh on the verify /
  // password step resumes there instead of dropping back to the phone step.
  const [restored] = React.useState<SignupSession | null>(() => readSignupSession());
  const inviteToken = navState.inviteToken ?? restored?.inviteToken;
  // Fall back to the durable copy the team-join page stashed, so the token still
  // applies if the wizard lost router state (e.g. an OTP page reload). Without
  // this the signup silently falls through to the "create a new org as owner"
  // path and the invited student ends up an owner instead of a candidate.
  const teamJoinToken = navState.teamJoinToken || sessionStorage.getItem('pending_team_join_token') || undefined;
  const orgInviteCode = navState.orgInviteCode || sessionStorage.getItem('pending_org_invite_code') || restored?.orgInviteCode || undefined;
  const searchParams = new URLSearchParams(location.search);
  const returnTo = learnerReturnPath(searchParams.get('returnTo') || (navState as { returnTo?: string })?.returnTo);
  // Referral code from a ?ref= link (or carried via nav state). Only honored on
  // the self-serve path; invite/team-join joiners ignore it.
  const referralCode =
    searchParams.get('ref')?.trim() || navState.referralCode;
  // Joiners keep a real name immediately and skip the onboarding gate; only they
  // collect a name in the wizard.
  const hasJoinToken = Boolean(inviteToken || teamJoinToken || orgInviteCode);

  const startRegister = useStartPhoneRegister();
  const resendRegister = useResendPhoneRegister();
  const verifyRegister = useVerifyPhoneRegister();
  const completeRegister = useCompletePhoneRegister();

  // Signup picker: Student (CANDIDATE, default) vs Teacher (STAFF, owns an org).
  // Only used for a tokenless signup — a join token forces CANDIDATE server-side.
  // examing.uz is learners only: organizations sign up on examing.io.
  const [accountType, setAccountType] = React.useState<'STAFF' | 'CANDIDATE'>(
    IS_IO ? (restored?.accountType ?? 'CANDIDATE') : 'CANDIDATE',
  );

  const [step, setStep] = React.useState<WizardStep>(restored?.step ?? 'phone');
  const [phone, setPhone] = React.useState(restored?.phone ?? '');
  // Masked destination (e.g. "+998 88 ••• 71 74") from the server — shown on the
  // verify step so users confirm the code went to the right number.
  const [phoneHint, setPhoneHint] = React.useState(restored?.phoneHint ?? '');
  const [challengeRef, setChallengeRef] = React.useState(restored?.challengeRef ?? '');
  // Set when the entered number already has an account (register start → 409),
  // so we can guide the user to log in / reset instead of dead-ending.
  const [existingPhone, setExistingPhone] = React.useState<string | null>(null);
  // Track Telegram login phase so the waiting card can take full width cleanly
  const [telegramPhase, setTelegramPhase] = React.useState<TelegramBotLoginPhase>('idle');
  const isTelegramActive = telegramPhase !== 'idle' && telegramPhase !== 'starting';

  // Phone registration is secondary: by default hidden behind a trigger button
  // unless user clicks it, is resuming an in-flight OTP session, or has an existing phone.
  const [showPhoneForm, setShowPhoneForm] = React.useState<boolean>(
    Boolean(restored || existingPhone),
  );

  // Verify-step state. Bumping resendNonce re-arms the OtpVerify cooldown.
  const [code, setCode] = React.useState('');
  const [verifyError, setVerifyError] = React.useState<string | null>(null);
  const [resendNonce, setResendNonce] = React.useState(0);

  // Only the 3 steps this wizard actually reaches — "details" lives on the
  // separate Onboarding page, so including it here left the stepper's last
  // dot permanently unreached (currentStepIndex never exceeds 2).
  const steps = [t('auth.step_phone'), t('auth.step_verify'), t('auth.step_password')];
  const currentStepIndex = step === 'phone' ? 0 : step === 'verify' ? 1 : 2;

  const phoneForm = useForm<PhoneFormData>({
    resolver: zodResolver(makePhoneSchema(t)),
    defaultValues: { phone: '' },
  });
  const passwordForm = useForm<PasswordFormData>({ resolver: zodResolver(makePasswordSchema(t)) });

  const toastError = (error: unknown, fallback: string) => {
    const err = error as { response?: { data?: { message?: string } } };
    toast.error(t('error'), { description: err?.response?.data?.message || fallback });
  };

  const onSubmitPhone = async (data: PhoneFormData) => {
    try {
      const res = await startRegister.mutateAsync({ phone: data.phone });
      setPhone(data.phone);
      setPhoneHint(res.phoneHint);
      setChallengeRef(res.challengeRef);
      setCode('');
      setVerifyError(null);
      setStep('verify');
      writeSignupSession({
        step: 'verify',
        phone: data.phone,
        phoneHint: res.phoneHint,
        challengeRef: res.challengeRef,
        accountType,
        inviteToken,
      });
    } catch (error) {
      // 409 = the phone already has an account. Guide instead of dead-ending.
      const status = (error as { response?: { status?: number } }).response?.status;
      if (status === 409) {
        setExistingPhone(data.phone);
        return;
      }
      toastError(error, t('auth.could_not_send_code'));
    }
  };

  const onVerify = async (codeValue: string) => {
    // Guard against double-fire: the OTP auto-submit races with button onClick.
    if (verifyRegister.isPending) return;
    try {
      await verifyRegister.mutateAsync({ challengeRef, code: codeValue });
      setVerifyError(null);
      setStep('password');
      writeSignupSession({ step: 'password', phone, phoneHint, challengeRef, accountType, inviteToken });
    } catch (error) {
      setVerifyError((error as { response?: { data?: { message?: string } } })?.response?.data?.message || t('auth.incorrect_code'));
      setCode('');
    }
  };

  const onResend = async () => {
    try {
      await resendRegister.mutateAsync({ challengeRef });
      setCode('');
      setVerifyError(null);
      setResendNonce((n) => n + 1);
      toast.success(t('success'), { description: t('auth.code_resent') });
    } catch (error) {
      toastError(error, t('auth.could_not_send_code'));
    }
  };

  const onSubmitPassword = async (data: PasswordFormData) => {
    if (hasJoinToken && (!data.name || data.name.trim().length < 2)) {
      passwordForm.setError('name', { type: 'manual', message: t('auth.name_min') });
      return;
    }
    try {
      await completeRegister.mutateAsync({
        challengeRef,
        password: data.password,
        ...(inviteToken
          ? { inviteToken, name: data.name }
          : teamJoinToken
            ? { teamJoinToken, name: data.name }
            : orgInviteCode
              ? { orgInviteCode, name: data.name }
              : { accountType, ...(referralCode ? { referralCode } : {}) }),
        ...acquisitionPayload(),
      });
      // The token has been consumed by the backend join — drop the durable copy.
      sessionStorage.removeItem('pending_team_join_token');
      sessionStorage.removeItem('pending_org_invite_code');
      clearSignupSession();
      // Joiners are profileComplete=true → straight to the app (which routes a
      // candidate to /portal). A tokenless Student is a CANDIDATE with no org and
      // no onboarding gate → portal. A tokenless Teacher creates an org and is
      // incomplete → the onboarding ("moderation") gate as the wizard's last step.
      if (hasJoinToken) {
        navigate('/app', { replace: true });
      } else if (accountType === 'CANDIDATE') {
        const target = returnTo
          ? `/onboarding?returnTo=${encodeURIComponent(returnTo)}`
          : '/onboarding';
        navigate(target, { replace: true, state: { fromSignup: true } });
      } else {
        navigate('/onboarding', { replace: true, state: { fromSignup: true } });
      }
    } catch (error) {
      toastError(error, t('auth.could_not_create_account'));
    }
  };

  const description =
    step === 'phone'
      ? undefined
      : step === 'verify'
        ? t('auth.enter_code_desc', { phone: phoneHint || phone })
        : t('auth.set_password_desc');

  return (
    <AuthSplitPanel
      showProofPanel={false}
      title={t('create_your_account')}
      subtitle={description}
    >
      {step !== 'phone' && (
        <div className="mb-7">
          <AuthStepper steps={steps} current={currentStepIndex} />
        </div>
      )}
      <div>
          {step === 'phone' && (
            <div className="space-y-6">
              {!hasJoinToken && IS_IO && (
                <AccountTypePicker value={accountType} onChange={setAccountType} />
              )}

              {/* examing.io: Google only. Phone/SMS and Telegram are Uzbek-market sign-ups. */}
              {IS_IO ? (
                <GoogleSignInButton
                  accountType={accountType}
                  mode="signup"
                  redirectTo={returnTo ?? '/app'}
                  inviteToken={inviteToken}
                  teamJoinToken={teamJoinToken}
                  orgInviteCode={orgInviteCode}
                  referralCode={referralCode}
                >
                  Google
                </GoogleSignInButton>
              ) : (<>
              {/* Primary 1-tap Authentication Options */}
              {isTelegramActive ? (
                <div className="w-full">
                  <TelegramBotLogin
                    accountType={accountType}
                    mode="signup"
                    redirectTo={returnTo ?? '/app'}
                    onPhaseChange={setTelegramPhase}
                  />
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {isTelegram() ? (
                    <TelegramMiniAppButton
                      accountType={accountType}
                      mode="signup"
                      redirectTo={returnTo ?? '/app'}
                      label="Telegram"
                    />
                  ) : (
                    <TelegramBotLogin
                      accountType={accountType}
                      mode="signup"
                      redirectTo={returnTo ?? '/app'}
                      label="Telegram"
                      onPhaseChange={setTelegramPhase}
                    />
                  )}

                  <GoogleSignInButton
                    accountType={accountType}
                    mode="signup"
                    redirectTo={returnTo ?? '/app'}
                    inviteToken={inviteToken}
                    teamJoinToken={teamJoinToken}
                    orgInviteCode={orgInviteCode}
                    referralCode={referralCode}
                  >
                    Google
                  </GoogleSignInButton>
                </div>
              )}

              {/* Divider between Primary and Secondary Auth */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground font-medium">
                    {t('auth.or_with_phone')}
                  </span>
                </div>
              </div>

              {/* Secondary Phone Auth */}
              {!showPhoneForm ? (
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  onClick={() => setShowPhoneForm(true)}
                  className="w-full font-semibold"
                >
                  <Phone className="mr-2 h-4 w-4 text-muted-foreground" />
                  <span>{t('auth.sign_up_with_phone')}</span>
                </Button>
              ) : (
                <div className="space-y-5 rounded-lg border border-border/80 bg-muted/20 p-5 animate-in fade-in slide-in-from-top-2 duration-200">
                  <div className="mb-2">
                    <AuthStepper steps={steps} current={currentStepIndex} />
                  </div>

                  <form onSubmit={phoneForm.handleSubmit(onSubmitPhone)} className="space-y-5">
                    <div className="space-y-2">
                      <Controller
                        name="phone"
                        control={phoneForm.control}
                        render={({ field, fieldState }) => (
                          <AuthPhoneField
                            autoFocus
                            label={t('auth.phone_number')}
                            value={field.value}
                            onChange={(next) => {
                              if (existingPhone) setExistingPhone(null);
                              field.onChange(next);
                            }}
                            onBlur={field.onBlur}
                            error={fieldState.error?.message}
                          />
                        )}
                      />
                      {!existingPhone && <p className="text-xs text-muted-foreground">{t('auth.phone_otp_hint')}</p>}
                    </div>

                    {existingPhone ? (
                      // The number is already registered, so sending a signup code is a
                      // dead end — swap the primary CTA for a clear path to sign in.
                      <div className="animate-in fade-in slide-in-from-top-1 space-y-4 rounded-lg border border-border bg-background p-4 duration-300">
                        <div className="flex items-start gap-3">
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-foreground/80">
                            <UserCheck className="h-5 w-5" aria-hidden />
                          </span>
                          <div className="space-y-0.5">
                            <p className="text-sm font-semibold text-foreground">{t('auth.phone_already_registered')}</p>
                            <p className="text-sm text-muted-foreground">{t('auth.phone_already_registered_desc')}</p>
                          </div>
                        </div>
                        <Button
                          type="button"
                          className="group w-full"
                          size="lg"
                          onClick={() => navigate('/login', { state: { phone: existingPhone } })}
                        >
                          {t('sign_in')}
                          <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                        </Button>
                        <button
                          type="button"
                          onClick={() => navigate('/forgot-password', { state: { phone: existingPhone } })}
                          className="block w-full text-center text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
                        >
                          {t('forgot_password')}
                        </button>
                      </div>
                    ) : (
                      <Button type="submit" className="group w-full" size="lg" disabled={startRegister.isPending}>
                        {startRegister.isPending ? t('auth.sending_code') : t('auth.send_code')}
                        {!startRegister.isPending && (
                          <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                        )}
                      </Button>
                    )}
                  </form>

                  {!existingPhone && (
                    <div className="text-center pt-1">
                      <button
                        type="button"
                        onClick={() => setShowPhoneForm(false)}
                        className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {t('cancel')}
                      </button>
                    </div>
                  )}
                </div>
              )}
              </>)}
            </div>
          )}

          {step === 'verify' && (
            <div className="space-y-6">
              <OtpVerify
                value={code}
                onChange={(next) => {
                  if (verifyError) setVerifyError(null);
                  setCode(next);
                }}
                onComplete={onVerify}
                error={verifyError}
                disabled={verifyRegister.isPending}
                onResend={onResend}
                resendPending={resendRegister.isPending}
                resendNonce={resendNonce}
                autoFocus
              />

              <Button
                type="button"
                className="w-full"
                size="lg"
                disabled={verifyRegister.isPending || code.length !== 6}
                onClick={() => onVerify(code)}
              >
                {verifyRegister.isPending ? t('auth.verifying') : t('auth.verify')}
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setStep('phone');
                    setShowPhoneForm(true);
                    setCode('');
                    setVerifyError(null);
                    clearSignupSession();
                  }}
                  className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  {t('auth.change_phone')}
                </button>
              </div>
            </div>
          )}

          {step === 'password' && (
            <form onSubmit={passwordForm.handleSubmit(onSubmitPassword)} className="space-y-5">
              <p className="text-sm font-medium text-foreground">{t('auth.set_password_title')}</p>

              {hasJoinToken && (
                <AuthField
                      type="text"
                  autoComplete="name"
                  label={t('full_name')}
                  placeholder={t('john_doe')}
                  error={passwordForm.formState.errors.name?.message}
                  {...passwordForm.register('name')}
                />
              )}

              <AuthPasswordField
                autoComplete="new-password"
                label={t('password')}
                placeholder={t('at_least_8_characters')}
                error={passwordForm.formState.errors.password?.message}
                {...passwordForm.register('password')}
              />

              <AuthPasswordField
                autoComplete="new-password"
                label={t('auth.confirm_password')}
                placeholder={t('auth.confirm_password')}
                error={passwordForm.formState.errors.confirmPassword?.message}
                {...passwordForm.register('confirmPassword')}
              />

              <Button type="submit" className="group w-full" size="lg" disabled={completeRegister.isPending}>
                {completeRegister.isPending ? t('creating_account') : t('create_account')}
                {!completeRegister.isPending && (
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                )}
              </Button>

              <div className="text-sm">
                <button
                  type="button"
                  onClick={() => setStep('verify')}
                  className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  {t('back')}
                </button>
              </div>
            </form>
          )}

        <p className="mt-6 text-center text-sm text-muted-foreground">
          {t('already_have_an_account')}{' '}
          <Link to={returnTo ? `/login?returnTo=${encodeURIComponent(returnTo)}` : '/login'} className="font-semibold text-foreground underline underline-offset-[3px] hover:decoration-2">
            {t('sign_in')}
          </Link>
        </p>
      </div>
    </AuthSplitPanel>
  );
};

export default Signup;
